<template>
  <div class="analytics-page">
    <div class="analytics-page__sticky-header">
      <div class="analytics-page__header">
        <e-icon name="graph" class="text-secondary mr-4" />
        <div class="caption">Аналитика</div>
        <v-spacer />
        <e-btn variant="context" size="M" append-icon-src="closeSvg" @click="closeDashboard"> Закрыть </e-btn>
      </div>
      <div class="analytics-page__subheader mt-4">
        <e-date-picker
          v-model="filters.date"
          :range="true"
          :max="new Date()"
          show-periods
          label="Период"
          class="analytics-page__filter mr-4"
          clearable
        />
      </div>
    </div>

    <page-content-placeholder
      v-if="isInitError"
      :img-src="PlaceholderImg"
      max-width="640px"
      text="Что-то пошло не так"
      sub-title="Проблемы в настройках модуля Аналитики, обратитесь в Поддержку"
    />

    <template v-else>
      <e-loader v-if="!isInitialized" :size="64" :width="6" class="analytics-page__loader text-blue" />
      <div
        v-show="isInitialized"
        ref="supersetContainerRef"
        class="analytics-page__superset-container"
        :key="dashboardKey"
        :style="{ height: `${dashboardHeight}px` }"
      />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { embedDashboard, type EmbeddedDashboard } from '@superset-ui/embedded-sdk';
import { useResizeObserver } from '@vueuse/core';
import { onBeforeUnmount, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useDisplay } from 'vuetify';
import PlaceholderImg from '@/assets/img/common/error-placeholder.svg';
import PageContentPlaceholder from '@/core/components/common/PageContentPlaceholder.vue';
import { EBtn, EDatePicker, EIcon, ELoader } from '@/core/components/ui';
import { useRouterBack } from '@/core/composables/useRouterBack';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { getIsoDateFormat } from '@/core/utils/date';
import { sleep } from '@/core/utils/sleep';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';

const supersetContainerRef = ref<HTMLDivElement>();
const { getAccomodationObjectDashboardAnalytics } = useAccommodationObjectsStore();
const { routerBack } = useRouterBack();

const $route = useRoute();

const filters = reactive<{
  date: Date[] | undefined;
}>({
  date: undefined,
});

const isInitialized = ref(false);
const isInitError = ref(false);

const $display = useDisplay();

const getGuestToken = async () => {
  const result = await getAccomodationObjectDashboardAnalytics();

  if (result?.data) {
    return result.data.accomodationObjectDashboardInfo.guestToken;
  }

  return '';
};

let dashboard: EmbeddedDashboard | undefined;
const dashboardKey = ref<string>();
let dashboardHeightInterval: number;

const dashboardHeight = ref<number>();

const setDashboardHeight = async () => {
  if (!dashboard) {
    return;
  }

  dashboardHeight.value = 0;

  await sleep(100);

  clearInterval(dashboardHeightInterval);

  dashboardHeight.value = (await dashboard.getScrollSize()).height;

  dashboardHeightInterval = window.setInterval(async () => {
    if (!dashboard) {
      return;
    }

    dashboardHeight.value = (await dashboard.getScrollSize()).height;
  }, 1000);
};

const closeDashboard = () => {
  routerBack({ name: RouteNames.ObjectInfo, params: { id: $route.params.id, acc_id: $route.params.acc_id } });
};

async function initSuperset(force?: boolean) {
  const result = await getAccomodationObjectDashboardAnalytics();

  if (result?.data) {
    try {
      const dashboardTokenInfo = result.data.accomodationObjectDashboardInfo;

      const newDashboardKey = $display.smAndDown.value
        ? 'AccomodationObjectDashboardMobile'
        : 'AccomodationObjectDashboard';

      if (newDashboardKey === dashboardKey.value && !force) {
        return;
      }

      isInitialized.value = false;

      dashboardKey.value = newDashboardKey;

      const dashboardId = dashboardTokenInfo.dashboards.find((x) => x.key == dashboardKey.value)?.value;

      if (!dashboardId) {
        console.error('Dashboard ID not found');
        return;
      }

      clearInterval(dashboardHeightInterval);

      dashboard?.unmount();

      await sleep(0);

      dashboard = await embedDashboard({
        id: dashboardId,
        supersetDomain: dashboardTokenInfo.supersetDomain,
        mountPoint: supersetContainerRef.value!,
        fetchGuestToken: async () => {
          if (!isInitialized.value && result?.data) {
            return result.data.accomodationObjectDashboardInfo.guestToken;
          }

          return getGuestToken();
        },
        dashboardUiConfig: {
          hideTitle: true,
          hideTab: false,
          hideChartControls: true,
          emitDataMasks: true,
          filters: {
            visible: false,
          },
          urlParams: {
            standalone: 1,
            native_filters: getFilterString(),
          },
        },
      });

      await setDashboardHeight();

      dashboard?.observeDataMask((e) => {
        console.log(e);
      });
    } catch (error) {
      console.error(error);
      isInitError.value = true;

      throw error;
    }
  } else {
    isInitError.value = true;
  }

  isInitialized.value = true;
}

const filterIds = {
  date: 'NATIVE_FILTER-i98qLowB7HcjSjOdk_je0',
};

watch(
  filters,
  async () => {
    initSuperset(true);
  },
  { deep: true },
);

function getFilterString() {
  const timeRange =
    filters.date && filters.date.length
      ? `${getIsoDateFormat(filters.date[0])} : ${getIsoDateFormat(filters.date[1])}`
      : undefined;

  return `(${filterIds.date}:(extraFormData:(time_range:'${timeRange}'),
  filterState:(validateStatus:!f,validateMessage:!f,value:'${timeRange}'),
  id:${filterIds.date},
  ownState:()))`
    .replace(/(\r\n|\n|\r)/gm, '')
    .replace(/\s{2}/g, '');
}

useResizeObserver(supersetContainerRef, () => {
  initSuperset();
});

onBeforeUnmount(() => {
  clearInterval(dashboardHeightInterval);
});
</script>

<style lang="scss" scoped>
.analytics-page {
  display: flex;
  flex-grow: 1;
  flex-direction: column;

  &__sticky-header {
    position: sticky;
    top: 0;
    z-index: 100;

    padding: 16px 32px;

    background-color: $body-bg-color;

    @include md-and-down {
      padding: 16px 24px;
    }

    @include xs {
      padding: 16px;
    }
  }

  &__header {
    display: flex;
    align-items: center;
  }

  &__subheader {
    display: flex;
    align-items: center;
  }

  &__filter {
    max-width: 280px;
  }

  &__loader {
    margin: auto;
  }

  &__superset-container {
    display: flex;
    flex-grow: 1;
    flex-direction: column;

    box-sizing: content-box;

    padding: 0 28px 20px;

    @include md-and-down {
      padding: 0 20px 20px;
    }

    @include xs {
      padding: 0 12px 12px;
    }

    :deep(iframe) {
      width: 100%;
      height: 100%;
      overflow: hidden;

      border: 0;
    }
  }
}
</style>
